import axios from "axios";
import api, { type PaginatedResponse } from "./api";

export interface ExtractionMetadata {
  started_at?: string | null;
  finished_at?: string | null;
  duration_seconds?: number | null;
  file_count?: number;
  uncompressed_bytes?: number;
  error_message?: string | null;
  skipped_reason?: string;
}

export interface PointCloud {
  id: string;
  virtual_site_id: string;
  name: string;
  description: string | null;
  s3_bucket: string;
  s3_key: string;
  s3_region: string;
  potree_version: string;
  point_count: number | null;
  file_size_bytes: number | null;
  bounds: Record<string, number> | null;
  coordinate_system: string | null;
  capture_date: string | null;
  status: "uploading" | "processing" | "ready" | "error" | "archived";
  zip_s3_key: string | null;
  potree_root_s3_key: string | null;
  extraction_metadata: ExtractionMetadata | null;
  panoramas: Array<unknown> | null;
  metadata_: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface PointCloudListItem {
  id: string;
  virtual_site_id: string;
  name: string;
  description: string | null;
  status: string;
  point_count: number | null;
  file_size_bytes: number | null;
  capture_date: string | null;
  panorama_count: number;
  created_at: string;
}

export interface PointCloudViewURL {
  point_cloud_id: string;
  /** Relative URL (path only) to the Potree metadata.json. The token is part
   *  of the path so Potree's relative-sibling fetches keep carrying it. Join
   *  with the API base URL before handing to Potree. */
  view_url: string | null;
  expires_in: number;
}

export interface PointCloudTag {
  id: string;
  asset_id: string;
  asset_name: string | null;
  asset_code: string | null;
  point_cloud_id: string | null;
  digital_twin_id: string | null;
  tag_type: "point" | "line" | "polygon" | "area";
  geometry: Record<string, unknown>;
  label: string | null;
  description: string | null;
  color: string | null;
  opacity: number;
  created_by: string | null;
  metadata_: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export async function listPointClouds(params: {
  virtual_site_id: string;
  page?: number;
  page_size?: number;
  search?: string;
  status?: string;
}): Promise<PaginatedResponse<PointCloudListItem>> {
  const res = await api.get("/point-clouds", { params });
  return res.data;
}

export async function getPointCloud(id: string): Promise<PointCloud> {
  const res = await api.get(`/point-clouds/${id}`);
  return res.data;
}

export interface CreatePointCloudPayload {
  virtual_site_id: string;
  name: string;
  description?: string;
  /** A Potree v2 .zip — must contain a metadata.json at the root or in one
   *  top-level folder. Server-side extraction validates this. */
  file: File;
}

interface InitiateUploadResponse {
  point_cloud_id: string;
  upload_url: string | null;
  s3_key: string;
  expires_in: number;
}

/**
 * Create a point cloud via the browser→S3 direct upload flow:
 *   1. POST /point-clouds/initiate-upload  -> presigned PUT URL + pc_id
 *   2. PUT the .zip to S3
 *   3. POST /point-clouds/{id}/complete-upload -> starts background extraction
 *
 * In dev mode the backend returns upload_url: null. We skip the PUT and go
 * straight to complete-upload (the backend's dev-mode short-circuit trusts
 * the declared size and flips status directly to 'ready').
 */
export async function createPointCloud(
  payload: CreatePointCloudPayload,
  onUploadProgress?: (loaded: number, total: number) => void,
  options?: { signal?: AbortSignal },
): Promise<PointCloud> {
  // Potree zip uploads use application/zip MIME by default; some browsers
  // detect it via file.type, others don't fill that in. Fall back to
  // application/zip explicitly so the signed PUT request matches.
  const contentType = payload.file.type || "application/zip";

  const initiateRes = await api.post<InitiateUploadResponse>(
    "/point-clouds/initiate-upload",
    {
      virtual_site_id: payload.virtual_site_id,
      name: payload.name,
      description: payload.description,
      filename: payload.file.name,
      file_size_bytes: payload.file.size,
      content_type: contentType,
    },
    { signal: options?.signal },
  );
  const { point_cloud_id, upload_url } = initiateRes.data;

  try {
    if (upload_url) {
      // PUT directly to S3 with raw axios (no auth interceptor — S3 rejects
      // requests carrying an unrelated Authorization header).
      await axios.put(upload_url, payload.file, {
        headers: { "Content-Type": contentType },
        onUploadProgress: (e) => {
          if (onUploadProgress && e.total) onUploadProgress(e.loaded, e.total);
        },
        signal: options?.signal,
        timeout: 0,
      });
    }
    const completeRes = await api.post<PointCloud>(
      `/point-clouds/${point_cloud_id}/complete-upload`,
      null,
      { signal: options?.signal },
    );
    return completeRes.data;
  } catch (err) {
    // Best-effort cleanup of the half-created row.
    try {
      await api.delete(`/point-clouds/${point_cloud_id}`);
    } catch {
      // swallow
    }
    throw err;
  }
}

/**
 * Files describing a raw-folder upload. `path` is the file's relative
 * location inside the Potree tree — for the canonical PotreeConverter v2
 * output that's `metadata.json`, `hierarchy.bin`, `octree.bin` (plus any
 * attributes/extra files that converter happens to emit).
 */
export interface FolderFile {
  path: string;
  file: File;
  /** Defaults to application/json for .json, application/octet-stream otherwise. */
  contentType?: string;
}

interface InitiateFolderUploadResponse {
  point_cloud_id: string;
  /** path → presigned PUT URL. Null in dev mode (caller skips the PUT). */
  upload_urls: Record<string, string | null>;
  s3_key_prefix: string;
  expires_in: number;
}

function _contentTypeFor(path: string, override?: string): string {
  if (override) return override;
  if (path.toLowerCase().endsWith(".json")) return "application/json";
  return "application/octet-stream";
}

/**
 * Create a point cloud from a raw folder of already-extracted Potree v2
 * files (no zip step). Flow:
 *   1. POST /point-clouds/initiate-folder-upload  (declares every file)
 *   2. PUT each file directly to its S3 presigned URL
 *   3. POST /point-clouds/{id}/complete-folder-upload (server HEAD-checks)
 *
 * `onUploadProgress` reports across the whole multi-file upload, not per
 * file — that's what the modal's progress bar wants.
 */
export async function createPointCloudFromFolder(
  payload: {
    virtual_site_id: string;
    name: string;
    description?: string;
    files: FolderFile[];
  },
  onUploadProgress?: (loaded: number, total: number) => void,
  options?: { signal?: AbortSignal },
): Promise<PointCloud> {
  if (!payload.files.length) {
    throw new Error("No files selected");
  }
  if (!payload.files.some((f) => f.path === "metadata.json")) {
    throw new Error("Folder must include a metadata.json at the root");
  }
  const declared = payload.files.map((f) => ({
    path: f.path,
    size: f.file.size,
    content_type: _contentTypeFor(f.path, f.contentType),
  }));

  const initiate = await api.post<InitiateFolderUploadResponse>(
    "/point-clouds/initiate-folder-upload",
    {
      virtual_site_id: payload.virtual_site_id,
      name: payload.name,
      description: payload.description,
      files: declared,
    },
    { signal: options?.signal },
  );
  const { point_cloud_id, upload_urls } = initiate.data;

  const totalBytes = payload.files.reduce((acc, f) => acc + f.file.size, 0);
  let loadedBytes = 0;
  // Tracks per-file partial progress so the aggregate progress callback
  // sees a smooth bytes-loaded counter as each upload streams.
  const perFile = new Map<string, number>();

  try {
    for (const f of payload.files) {
      const url = upload_urls[f.path];
      const ct = _contentTypeFor(f.path, f.contentType);
      if (url) {
        await axios.put(url, f.file, {
          headers: { "Content-Type": ct },
          onUploadProgress: (e) => {
            perFile.set(f.path, e.loaded);
            if (onUploadProgress) {
              loadedBytes = 0;
              perFile.forEach((v) => { loadedBytes += v; });
              onUploadProgress(loadedBytes, totalBytes);
            }
          },
          signal: options?.signal,
          timeout: 0,
        });
      }
      // Even in dev mode (url=null), report the file as "done" so the
      // progress bar reaches 100%.
      perFile.set(f.path, f.file.size);
      if (onUploadProgress) {
        loadedBytes = 0;
        perFile.forEach((v) => { loadedBytes += v; });
        onUploadProgress(loadedBytes, totalBytes);
      }
    }
    const complete = await api.post<PointCloud>(
      `/point-clouds/${point_cloud_id}/complete-folder-upload`,
      null,
      { signal: options?.signal },
    );
    return complete.data;
  } catch (err) {
    try {
      await api.delete(`/point-clouds/${point_cloud_id}`);
    } catch {
      // swallow
    }
    throw err;
  }
}

export async function deletePointCloud(id: string): Promise<void> {
  await api.delete(`/point-clouds/${id}`);
}

export async function getViewUrl(id: string): Promise<PointCloudViewURL> {
  const res = await api.get(`/point-clouds/${id}/view-url`);
  return res.data;
}

export async function reprocessPointCloud(id: string): Promise<PointCloud> {
  const res = await api.post(`/point-clouds/${id}/reprocess`);
  return res.data;
}

export async function listTagsForPointCloud(
  pointCloudId: string,
): Promise<{ point_cloud_id: string; tags: PointCloudTag[]; total: number }> {
  const res = await api.get(`/tags/by-point-cloud/${pointCloudId}`);
  return res.data;
}

export interface CreateTagPayload {
  asset_id: string;
  point_cloud_id: string;
  tag_type: "point" | "line" | "polygon" | "area";
  geometry: Record<string, unknown>;
  label?: string;
  description?: string;
  color?: string;
}

export async function createTag(payload: CreateTagPayload): Promise<PointCloudTag> {
  const res = await api.post("/tags", null, {
    params: {
      asset_id: payload.asset_id,
      point_cloud_id: payload.point_cloud_id,
      tag_type: payload.tag_type,
      geometry: JSON.stringify(payload.geometry),
      label: payload.label,
      description: payload.description,
      color: payload.color,
    },
  });
  return res.data;
}

export async function deleteTag(tagId: string): Promise<void> {
  await api.delete(`/tags/${tagId}`);
}

export interface UpdateTagPayload {
  asset_id?: string;
  label?: string | null;
  color?: string;
}

export async function updateTag(tagId: string, payload: UpdateTagPayload): Promise<PointCloudTag> {
  const res = await api.put(`/tags/${tagId}`, null, {
    params: {
      asset_id: payload.asset_id,
      label: payload.label,
      color: payload.color,
    },
  });
  return res.data;
}

/** Visually distinct tag colors (same palette as digital twins for consistency). */
const TAG_COLOR_PALETTE = [
  "#ef4444", "#f97316", "#eab308", "#22c55e", "#06b6d4",
  "#3b82f6", "#a855f7", "#ec4899", "#10b981", "#f59e0b",
];

export function pickRandomTagColor(usedColors: ReadonlyArray<string | null | undefined> = []): string {
  const used = new Set(
    usedColors
      .filter((c): c is string => typeof c === "string")
      .map((c) => c.toLowerCase()),
  );
  const fresh = TAG_COLOR_PALETTE.filter((c) => !used.has(c.toLowerCase()));
  const pool = fresh.length > 0 ? fresh : TAG_COLOR_PALETTE;
  return pool[Math.floor(Math.random() * pool.length)];
}

/** Resolve the API base URL the same way `lib/api.ts` does so view URLs the
 *  backend returns as relative paths can be turned into absolute fetch URLs. */
export function getApiBaseUrl(): string {
  return (import.meta.env.VITE_API_BASE_URL as string | undefined) || "http://localhost:8000";
}
